import React, { FC, useState } from 'react';
import { IReviewNewProps } from '../../../shared/interfaces/IReviewNew';
import ReviewWrapper from '../ReviewWrapper';
import { Box, Button, Divider, Typography, useTheme } from '@mui/material';
import { ExpandLess, LocationOn, MoreVert, Person } from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import Block from '../../Block/Block';
import DataShakeSummary from '../DataShakeSummary';

interface Props extends IReviewNewProps {}

const GlassdoorReview: FC<Props> = ({
  commentingModule,
  communityManagement,
  hideHeader,
  hideFooter,
  statusModule,
  status,
  deletionModule,
  reviewEntry,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [showAllBlocks, setShowAllBlocks] = useState(false);

  const {
    rating,
    header,
    position,
    location,
    employmentStatus,
    textCompanyPros,
    textCompanyCons,
    blocks,
    companyComment,
    reviewLink,
    metaData,
  } = reviewEntry.review;

  return (
    <ReviewWrapper
      communityManagement={communityManagement}
      commentingModule={commentingModule}
      hideHeader={hideHeader}
      hideFooter={hideFooter}
      rating={rating}
      reviewLink={reviewLink}
      statusModule={statusModule}
      status={status}
      deletionModule={deletionModule}
      reviewEntry={reviewEntry}
    >
      {position && (
        <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
          <Person fontSize="small" />
          <Typography
            variant="caption"
            sx={{
              ml: theme.spacing(1),
            }}
          >
            {position} {employmentStatus && '('}
            {employmentStatus && <Trans t={t}>{employmentStatus}</Trans>}
            {employmentStatus && ')'}
          </Typography>
        </Box>
      )}
      {location && (
        <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
          <LocationOn fontSize="small" />
          <Typography
            variant="caption"
            sx={{
              ml: theme.spacing(1),
            }}
          >
            {location}
          </Typography>
        </Box>
      )}
      {header && (
        <Typography
          variant="h6"
          sx={{
            mt: theme.spacing(2),
          }}
        >
          {header}
        </Typography>
      )}
      {textCompanyPros && (
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 700 }}
          >
            {t('pros')} prospros
          </Typography>
          <Typography variant="body1">{textCompanyPros}</Typography>
        </Box>
      )}
      {textCompanyCons && (
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 700 }}
          >
            {t('contras')} conscons
          </Typography>
          <Typography variant="body1">{textCompanyCons}</Typography>
        </Box>
      )}
      {blocks && blocks.length > 0 && (
        <Box sx={{ mt: theme.spacing(2) }}>
          {showAllBlocks ? (
            blocks.map(({ rating, text, title }, index) => {
              return (
                <Box
                  key={index}
                  sx={{ mt: theme.spacing(1) }}
                >
                  bwock
                  <Divider orientation="horizontal" />
                  <Block
                    rating={rating}
                    text={text}
                    title={title}
                  />
                </Box>
              );
            })
          ) : (
            <Box sx={{ mt: theme.spacing(1) }}>
              <Divider orientation="horizontal" />
              <Block
                rating={blocks.filter((value) => typeof value.rating === 'number')[0].rating}
                text={blocks.filter((value) => typeof value.rating === 'number')[0].text}
                title={blocks.filter((value) => typeof value.rating === 'number')[0].title}
              />
            </Box>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={showAllBlocks ? <ExpandLess /> : <MoreVert />}
            onClick={() => setShowAllBlocks(!showAllBlocks)}
            sx={{
              my: theme.spacing(1),
            }}
          >
            {showAllBlocks ? t('show_less') : t('show_more')}
          </Button>
        </Box>
      )}
      {metaData && <DataShakeSummary reviewEntry={reviewEntry} />}
    </ReviewWrapper>
  );
};

export default GlassdoorReview;
